import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import Step1StudentInfo from '../features/forms/Step1StudentInfo';
import Step2ParentInfo from '../features/forms/Step2ParentInfo';
import Step3ClassroomRequest from '../features/forms/Step3ClassroomRequest';
import Step4FinalQuestions from '../features/forms/Step4FinalQuestions';
import ThankYouPage from '../features/forms/ThankYouPage';
import FormNavigationButton from '../ui/FormNavigationButton';
import FormProgressHeader from '../features/forms/FormProgressHeader';
import FormHeader from '../features/forms/FormHeader';
import {
  ApplicationData,
  ApplicationErrors,
  StudentErrors,
  ParentErrors,
  ClassroomRequestErrors,
  FinalQuestionsErrors
} from '../../types/applicationTypes';

export default function ApplicationForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<ApplicationErrors>({});
  const [applicationData, setApplicationData] = useState<ApplicationData>({
    student: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      homeAddress: '',
      city: '',
      zipCode: ''
    },
    parent: {
      parentFirstName: '',
      parentLastName: '',
      parentEmail: '',
      parentPhone: '',
      workAddress: '',
      workPhone: '',
      relationship: ''
    },
    parent2: {
      parentFirstName: '',
      parentLastName: '',
      parentEmail: '',
      parentPhone: '',
      workAddress: '',
      workPhone: '',
      relationship: ''
    },
    classroomRequest: {
      classroom: '',
      requestedDays: '',
      startDate: '',
      spacePreference: ''
    },
    finalQuestions: {
      howDidYouHear: '',
      acknowledgment: false
    }
  });

  const totalSteps = 4;
  const completionPercentage = ((currentStep - 1) / totalSteps) * 100;

  const hasErrors = (nextErrors: ApplicationErrors): boolean => {
    return Object.values(nextErrors).some((section) =>
      section && Object.values(section).some((value) => Boolean(value))
    );
  };

  const clearErrorsForUpdates = (updates: Partial<ApplicationData>) => {
    setErrors((prev) => {
      const next: ApplicationErrors = { ...prev };

      (Object.keys(updates) as Array<keyof ApplicationData>).forEach((section) => {
        const sectionUpdates = updates[section];
        const sectionErrors = next[section as keyof ApplicationErrors];

        if (!sectionUpdates || !sectionErrors) {
          return;
        }

        const sectionErrorsCopy = { ...sectionErrors } as Record<string, string | undefined>;
        Object.keys(sectionUpdates as object).forEach((field) => {
          delete sectionErrorsCopy[field];
        });

        if (Object.keys(sectionErrorsCopy).length === 0) {
          delete next[section as keyof ApplicationErrors];
        } else {
          next[section as keyof ApplicationErrors] = sectionErrorsCopy as typeof sectionErrors;
        }
      });

      return next;
    });
  };

  // Load saved form data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('hcc-application-form');
    const savedStep = localStorage.getItem('hcc-application-step');
    
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setApplicationData(parsedData);
      } catch (error) {
        console.error('Error loading saved form data:', error);
      }
    }
    
    if (savedStep) {
      setCurrentStep(parseInt(savedStep, 10));
    }
  }, []);

  // Save form data to localStorage whenever it changes -  
  useEffect(() => {
    localStorage.setItem('hcc-application-form', JSON.stringify(applicationData));
    localStorage.setItem('hcc-application-step', currentStep.toString());
  }, [applicationData, currentStep]);

  const handleInputChange = (section: keyof ApplicationData, field: string, value: string | boolean) => {
    setApplicationData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Update data function for step components
  const updateData = (updates: Partial<ApplicationData>) => {
    setApplicationData(prev => ({
      ...prev,
      ...updates
    }));
    clearErrorsForUpdates(updates);
  };

  const getStepErrors = (step: number): ApplicationErrors => {
    const nextErrors: ApplicationErrors = {};

    switch (step) {
      case 1:
        const studentErrors: StudentErrors = {};
        if (!applicationData.student.firstName.trim()) studentErrors.firstName = 'First name is required.';
        if (!applicationData.student.lastName.trim()) studentErrors.lastName = 'Last name is required.';
        if (!applicationData.student.dateOfBirth.trim()) studentErrors.dateOfBirth = 'Date of birth is required.';
        if (!applicationData.student.gender) studentErrors.gender = 'Please select a gender.';
        if (!applicationData.student.homeAddress.trim()) studentErrors.homeAddress = 'Home address is required.';
        if (!applicationData.student.city.trim()) studentErrors.city = 'City is required.';
        if (!applicationData.student.zipCode.trim()) studentErrors.zipCode = 'Zip code is required.';

        if (Object.keys(studentErrors).length > 0) {
          nextErrors.student = studentErrors;
        }
        return nextErrors;
      case 2:
        const parentErrors: ParentErrors = {};
        if (!applicationData.parent.parentFirstName.trim()) parentErrors.parentFirstName = 'First name is required.';
        if (!applicationData.parent.parentLastName.trim()) parentErrors.parentLastName = 'Last name is required.';
        if (!applicationData.parent.parentEmail.trim()) parentErrors.parentEmail = 'Email is required.';
        if (!applicationData.parent.parentPhone.trim()) parentErrors.parentPhone = 'Phone is required.';
        if (!applicationData.parent.relationship.trim()) parentErrors.relationship = 'Relationship is required.';

        if (Object.keys(parentErrors).length > 0) {
          nextErrors.parent = parentErrors;
        }
        return nextErrors;
      case 3:
        const classroomErrors: ClassroomRequestErrors = {};
        if (!applicationData.classroomRequest.classroom.trim()) classroomErrors.classroom = 'Please select a classroom.';
        if (!applicationData.classroomRequest.requestedDays.trim()) classroomErrors.requestedDays = 'Please choose requested days.';
        if (!applicationData.classroomRequest.spacePreference.trim()) classroomErrors.spacePreference = 'Please choose a space preference.';

        if (Object.keys(classroomErrors).length > 0) {
          nextErrors.classroomRequest = classroomErrors;
        }
        return nextErrors;
      case 4:
        const finalErrors: FinalQuestionsErrors = {};
        if (!applicationData.finalQuestions.howDidYouHear.trim()) finalErrors.howDidYouHear = 'This field is required.';
        if (!applicationData.finalQuestions.acknowledgment) finalErrors.acknowledgment = 'You must acknowledge the terms.';

        if (Object.keys(finalErrors).length > 0) {
          nextErrors.finalQuestions = finalErrors;
        }
        return nextErrors;
      default:
        return nextErrors;
    }
  };

  const handleContinue = () => {
    const nextErrors = getStepErrors(currentStep);
    setErrors(nextErrors);

    if (!hasErrors(nextErrors)) {
      if (currentStep === totalSteps) {
        // Submit form
        handleSubmit();
      } else {
        setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      }
    }
  };

  const handleSubmit = async () => {
    let phpSuccess = false;
    let sheetsSuccess = false;

    try {
      // 1. Submit to PHP backend for email
      console.log('Submitting to PHP backend...');
      try {
        const phpResponse = await fetch('/api/submit-application.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(applicationData)
        });

        if (phpResponse.ok) {
          const phpResult = await phpResponse.json();
          console.log('PHP submission successful:', phpResult);
          phpSuccess = true;
        } else {
          console.error('PHP submission failed:', phpResponse.status);
        }
      } catch (phpError) {
        console.error('PHP submission error:', phpError);
      }

      // 2. Submit to Google Sheets as backup/secondary storage
      console.log('Submitting to Google Sheets...');
      try {
        const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyirSJFQgdukDq3xcMoB8lZNQ2TEQkw8e8JImfXEU4Zv846z6KjZfmtpPIvt3cKI0-T/exec';
        
        await fetch(GOOGLE_SCRIPT_URL, {
          method: 'POST',
          mode: 'no-cors', // Required for Google Apps Script
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(applicationData)
        });

        console.log('Google Sheets submission completed');
        sheetsSuccess = true; // Assume success with no-cors mode
      } catch (sheetsError) {
        console.error('Google Sheets submission error:', sheetsError);
      }

      // Show success if at least one method worked
      if (phpSuccess || sheetsSuccess) {
        console.log(`Submission successful! PHP: ${phpSuccess}, Sheets: ${sheetsSuccess}`);
        
        // Show thank you page
        setIsSubmitted(true);
        
        // Clear saved form data from localStorage
        localStorage.removeItem('hcc-application-form');
        localStorage.removeItem('hcc-application-step');
      } else {
        throw new Error('Both submission methods failed');
      }
      
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your application. Please call us at 604.986.2388 or email admissions@harboursidechildren.ca for assistance.');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const clearForm = () => {
    if (window.confirm('Are you sure you want to clear all form data? This cannot be undone.')) {
      localStorage.removeItem('hcc-application-form');
      localStorage.removeItem('hcc-application-step');
      window.location.reload(); // Reload to reset state
    }
  };

  // Show thank you page if submitted
  if (isSubmitted) {
    return <ThankYouPage applicationData={applicationData} />;
  }

  return (
    <div className="min-h-screen bg-cover bg-center relative" 
         style={{ backgroundImage: "url('/images/kidsattable.jpg')" }}>
      {/* Background overlay */}
      <div className="absolute inset-0 bg-black/20"></div>
      
      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
        <motion.div 
          className="w-full max-w-4xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <FormHeader />

          {/* Form Container */}
          <motion.div 
            className="bg-white rounded-2xl shadow-lg overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Progress Header */}
            <FormProgressHeader 
              currentStep={currentStep}
              totalSteps={totalSteps}
              completionPercentage={completionPercentage}
              onClearForm={clearForm}
            />

            {/* Step Content */}
            <div className="p-6">
              {currentStep === 1 && (
                <Step1StudentInfo data={applicationData} errors={errors} updateData={updateData} />
              )}

              {/* Step 2: Parent Information */}
              {currentStep === 2 && (
                <Step2ParentInfo data={applicationData} errors={errors} updateData={updateData} />
              )}

              {/* Step 3: Classroom Request */}
              {currentStep === 3 && (
                <Step3ClassroomRequest data={applicationData} errors={errors} updateData={updateData} />
              )}

              {/* Step 4: Final Questions */}
              {currentStep === 4 && (
                <Step4FinalQuestions data={applicationData} errors={errors} updateData={updateData} />
              )}


              {/* Other steps placeholder */}
              {currentStep > 4 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4 }}
                  className="text-center py-12"
                >
                  <h3 className="text-xl font-semibold text-gray-600 mb-4">
                    Step {currentStep} Content
                  </h3>
                  <p className="text-gray-500">
                    Additional steps are being developed.
                  </p>
                </motion.div>
              )}
            </div>

            {/* Navigation */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
              <FormNavigationButton
                type="previous"
                onClick={handlePrevious}
                disabled={currentStep === 1}
              />

              <FormNavigationButton
                type={currentStep === totalSteps ? 'submit' : 'continue'}
                onClick={handleContinue}
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
